import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, Info, KeyRound, Loader2, RefreshCw,
  ShieldAlert, ShieldCheck, Truck, XCircle,
} from "lucide-react";
import api from "../lib/api";
import { InternalAdminLayout } from "./InternalAdmin";

// Internal Admin — Uber Direct PRODUCTION onboarding (Sep 2026).
//
// The backend has carried a complete production cutover path for some
// time — staging, preflight, activation and an emergency stop — and no
// interface reached any of it. The only way to run a cutover was a
// hand-made authenticated request, which is exactly the situation that
// puts a production credential into a shell history.
//
// This page is that interface, and nothing more. Every rule already
// lives on the server: which checks block, what a complete credential
// set is, whether activation is permitted. Nothing here re-decides any
// of it — the page renders what the server returns and refuses to
// enable a control the server has not said is safe.
//
// Deliberately NOT on the tenant's own Local Delivery page. That page
// configures pickup details and TEST credentials, and its credential
// route forces `mode: sandbox` on every write, so a tenant can never
// reach production by any path. Production belongs to a super-admin.
//
// Secret handling, in one place so it can be checked in one place:
//   * the four inputs are `type="password"`, `autoComplete="off"`;
//   * their values live in component state for the life of one submit
//     and are cleared the moment the server accepts them;
//   * nothing is written to localStorage, sessionStorage or a query
//     string, and nothing is logged;
//   * the server never returns a stored value — only presence booleans
//     — so there is no value here to leak back into the DOM.

const CREDENTIAL_FIELDS = [
  { key: "customer_id", label: "Production Customer ID" },
  { key: "client_id", label: "Production Client ID" },
  { key: "client_secret", label: "Production Client Secret" },
  { key: "webhook_signing_key", label: "Production Webhook Signing Key" },
];

// The seven facts the server cannot establish on its own. Every one is
// required and every one must be true — `ActivationAttestations` has no
// defaults, so an omitted attestation is a rejection, not a silent false.
const ATTESTATIONS = [
  { key: "uber_production_account_verified",
    label: "The merchant's Uber Direct PRODUCTION account exists and is the one these credentials belong to" },
  { key: "uber_billing_verified",
    label: "Billing is enabled on that Uber account — real deliveries will be charged to the merchant" },
  { key: "uber_merchant_location_verified",
    label: "The merchant's pickup location is registered and correct in Uber" },
  { key: "uber_webhook_registered",
    label: "The production webhook is registered in Uber's dashboard and its signing key is the one staged here" },
  { key: "production_credentials_attested",
    label: "The four values staged are PRODUCTION credentials, not test credentials" },
  { key: "stripe_live_service_wide_accepted",
    label: "Stripe runs service-wide, not per tenant — activating here accepts that this service's Stripe mode applies" },
  { key: "no_customer_checkout_in_progress_confirmed",
    label: "No customer checkout is in progress for this tenant right now" },
];

const REFERENCE_PATTERN = /^[A-Z0-9][A-Z0-9-]{2,62}[A-Z0-9]$/;

// The four policies `ActivateProductionIn.expected_policies` requires,
// in the order the backend's own `EXPECTED_POLICY_FIELDS` names them.
const POLICY_FIELDS = [
  { key: "min_order_value", label: "Minimum order value", kind: "number" },
  { key: "max_delivery_fee", label: "Maximum delivery fee", kind: "number" },
  { key: "address_validation", label: "Address validation", kind: "boolean" },
  { key: "max_delivery_radius", label: "Maximum delivery radius", kind: "number" },
];

// `_build_quote_policies` reports a policy as ready, not_configured,
// malformed or configured_but_unenforceable. `compare_expected_quote_
// policies` accepts "ready" (authorized by its exact value) and, for the
// three numeric thresholds only, "not_configured" (authorized by an
// explicit `null`: no Rudras-AI limit, exactly as the quote path treats
// an unset value). Malformed and unenforceable are never authorizable,
// and this page must not treat "not in unready_policies" as good enough.
const POLICY_STATUS_READY = "ready";
const POLICY_STATUS_NOT_CONFIGURED = "not_configured";

function isAuthorizableStatus(field, status) {
  if (status === POLICY_STATUS_READY) return true;
  return field.kind === "number" && status === POLICY_STATUS_NOT_CONFIGURED;
}

/** Build the `expected_policies` body from the REAL preflight response.
 *
 *  The response nests each policy under `quote_policies.policies.<name>`
 *  as an object — `{value, unit, source, status, …}` — and the value the
 *  server compares against is `.value`. A previous version of this page
 *  read `quote_policies.<name>` directly, which is `undefined`, so
 *  `Number(...)` produced `NaN`, `JSON.stringify` sent `null`, and the
 *  strict `ExpectedQuotePolicies` model refused every activation with a
 *  422 whose field names the credential-safe route redacts. The operator
 *  saw `invalid_request_body` and had no way to act on it.
 *
 *  Returns `null` rather than a defaulted body whenever any policy is
 *  missing, not authorizable, or does not carry a usable value. A numeric
 *  policy the tenant left unset is sent as an explicit `null` ("no
 *  Rudras-AI limit") — never as 0, and never invented. Nothing here
 *  invents a threshold to get past validation: an unauthorizable policy
 *  must block activation, not be papered over.
 */
export function buildExpectedPolicies(quotePolicies) {
  const rows = (quotePolicies || {}).policies;
  if (!rows || typeof rows !== "object") return null;
  const out = {};
  for (const field of POLICY_FIELDS) {
    const policy = rows[field.key];
    if (!policy || typeof policy !== "object") return null;
    if (!isAuthorizableStatus(field, policy.status)) return null;
    if (policy.status === POLICY_STATUS_NOT_CONFIGURED) {
      if (policy.value !== null) return null;
      out[field.key] = null;
    } else if (field.kind === "boolean") {
      if (typeof policy.value !== "boolean") return null;
      out[field.key] = policy.value;
    } else {
      const value = policy.value;
      // `strict=True` on the server: a JSON string or a boolean is a
      // rejection there, so it must be a rejection here too.
      if (typeof value !== "number" || !Number.isFinite(value)) return null;
      out[field.key] = value;
    }
  }
  return out;
}

/** Every policy the operator cannot authorize, with the server's own
 *  reason: anything in `unready_policies` (malformed or unenforceable)
 *  and any policy whose status the activation comparison refuses —
 *  everything except "ready" and, for a numeric threshold,
 *  "not_configured". */
export function unauthorizablePolicies(quotePolicies) {
  const qp = quotePolicies || {};
  const rows = qp.policies && typeof qp.policies === "object" ? qp.policies : null;
  const unready = new Set(Array.isArray(qp.unready_policies) ? qp.unready_policies : []);
  const out = [];
  for (const field of POLICY_FIELDS) {
    const policy = rows ? rows[field.key] : null;
    const status = policy && typeof policy === "object" ? policy.status : undefined;
    if (isAuthorizableStatus(field, status) && !unready.has(field.key)) continue;
    out.push({
      key: field.key,
      label: field.label,
      status: status || (rows ? "missing_from_preflight" : "no_policies_in_preflight_response"),
    });
  }
  return out;
}

function Severity({ check }) {
  const sev = check.severity || "error";
  if (check.ok) {
    return <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-label="pass" />;
  }
  if (sev === "info") {
    return <Info className="h-4 w-4 shrink-0 text-sky-600" aria-label="informational" />;
  }
  if (sev === "warning") {
    return <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" aria-label="warning" />;
  }
  return <XCircle className="h-4 w-4 shrink-0 text-red-600" aria-label="fail" />;
}

/** A blocking failure is an `error`-severity check the server marked not-ok.
 *  `overall` already encodes this, but a reader of the list should be able
 *  to see WHICH row is the blocker without recomputing the rule. */
function isBlocking(check) {
  return !check.ok && (check.severity || "error") === "error";
}

function ConfirmDialog({ open, title, tone = "danger", body, confirmLabel, onCancel, onConfirm, busy }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-lg border bg-card p-5 shadow-lg" data-testid="uber-prod-confirm">
        <h3 className={"flex items-center gap-2 text-lg font-semibold " + (tone === "danger" ? "text-red-700" : "")}>
          {tone === "danger" ? <ShieldAlert className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
          {title}
        </h3>
        <div className="mt-3 space-y-2 text-sm">{body}</div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={busy}
            className="rounded-md border px-3 py-1.5 text-sm" data-testid="uber-prod-confirm-cancel">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} disabled={busy}
            data-testid="uber-prod-confirm-accept"
            className={"rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60 "
              + (tone === "danger" ? "bg-red-600" : "bg-stone-900")}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InternalUberProductionOnboarding() {
  const [tenants, setTenants] = useState([]);
  const [query, setQuery] = useState("");
  const [company, setCompany] = useState(null);      // { id, name }
  const [status, setStatus] = useState(null);        // GET production-credentials
  const [selector, setSelector] = useState(null);    // catalog-selector
  const [catalogId, setCatalogId] = useState("");
  const [preflight, setPreflight] = useState(null);
  const [busy, setBusy] = useState("");
  const [secrets, setSecrets] = useState({});
  const [reference, setReference] = useState("");
  // The emergency stop gets its own field. Sharing the activation
  // section's reference meant the control an operator needs DURING an
  // incident was enabled by typing into a panel about a different,
  // already-completed action — and `doActivate` clears that field on
  // success, so the stop was left disabled exactly when it mattered.
  const [stopReference, setStopReference] = useState("");
  const [activationId, setActivationId] = useState("");
  const [attested, setAttested] = useState({});
  const [confirm, setConfirm] = useState(null);
  const liveRef = useRef(true);
  useEffect(() => () => { liveRef.current = false; }, []);
  // The tenant every in-flight response must still belong to. A reply
  // that arrives after the operator switched tenant is dropped, never
  // rendered under the new tenant's name.
  const companyRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const params = query.trim() ? { q: query.trim() } : undefined;
        const { data } = await api.get("/internal-admin/tenants", { params });
        if (alive) setTenants(Array.isArray(data) ? data : []);
      } catch (err) {
        if (alive) toast.error(err.response?.data?.detail || "Failed to load tenants");
      }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [query]);

  /** Authoritative server state for the selected tenant. Called after
   *  every mutation — the page never infers a new state from the fact
   *  that a request succeeded. */
  const refreshStatus = useCallback(async (companyId) => {
    if (!companyId) return;
    // Settled independently: the staged-set status and the provider
    // state are separate facts. One failing read must not discard the
    // other — above all, a failed staging read must not hide a live
    // tenant's provider state or its Emergency Stop.
    const [stRes, selRes] = await Promise.allSettled([
      api.get("/internal-admin/uber-direct/production-credentials", { params: { company_id: companyId } }),
      api.post("/internal-admin/uber-direct/catalog-selector", { company_id: companyId }),
    ]);
    if (!liveRef.current || companyRef.current !== companyId) return;
    // A failed read becomes UNKNOWN (null), never the previous answer:
    // a stale "stopped" left on screen is as wrong as an invented one.
    setStatus(stRes.status === "fulfilled" ? stRes.value.data : null);
    const sel = selRes.status === "fulfilled" ? selRes.value.data : null;
    setSelector(sel);
    for (const r of [stRes, selRes]) {
      if (r.status === "rejected") {
        toast.error(r.reason?.response?.data?.detail || "Failed to read production status — shown as UNKNOWN");
      }
    }
    // Auto-select only a catalog the activation guard can actually
    // accept. `_check_catalog` blocks an archived or delivery-disabled
    // catalog server-side, so pre-filling one only guarantees a failed
    // preflight in the middle of a cutover.
    const usable = (sel?.catalogs || []).filter(
      (c) => c.delivery_enabled !== false && c.archived !== true);
    const only = usable.length === 1 ? usable[0] : null;
    setCatalogId((prev) => prev || only?.id || only?.catalog_id || "");
  }, []);

  const selectTenant = useCallback(async (t) => {
    // Every per-tenant value resets here. Carrying a preflight, a
    // staged-set status or a catalog id across a tenant switch is how
    // an operator ends up activating the wrong merchant.
    companyRef.current = t.id;
    setCompany({ id: t.id, name: t.name || t.company_name || t.id });
    setStatus(null); setSelector(null); setCatalogId("");
    setPreflight(null); setSecrets({}); setAttested({});
    setReference(""); setStopReference(""); setActivationId("");
    await refreshStatus(t.id);
  }, [refreshStatus]);

  const allSecretsPresent = CREDENTIAL_FIELDS.every((f) => (secrets[f.key] || "").trim().length > 0);

  const doStage = async () => {
    setBusy("stage");
    try {
      await api.post("/internal-admin/uber-direct/production-credentials", {
        company_id: company.id,
        client_id: secrets.client_id,
        client_secret: secrets.client_secret,
        customer_id: secrets.customer_id,
        webhook_signing_key: secrets.webhook_signing_key,
      });
      setSecrets({});                       // cleared the moment the server accepts them
      toast.success(productionLive
        ? "Replacement credentials staged. Production stays LIVE on the current credentials."
        : "Production credentials staged. Production is NOT active.");
      await refreshStatus(company.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not stage credentials");
    } finally { setBusy(""); setConfirm(null); }
  };

  const doPreflight = async () => {
    setBusy("preflight");
    const forCompany = company.id;
    try {
      const { data } = await api.post("/internal-admin/uber-direct/preflight", {
        company_id: forCompany, ...(catalogId ? { catalog_id: catalogId } : {}),
      });
      if (companyRef.current === forCompany) setPreflight(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preflight failed to run");
    } finally { setBusy(""); }
  };

  const doActivate = async () => {
    setBusy("activate");
    try {
      if (!expectedPolicies) {
        // Unreachable through the button (`canActivate` requires it),
        // kept because sending a defaulted body would be worse than
        // refusing here.
        toast.error("Preflight did not report an authorizable set of quote policies.");
        return;
      }
      await api.post(
        `/internal-admin/uber-direct/production-credentials/${status.credential_set.credential_set_id}/activate`,
        {
          company_id: company.id,
          catalog_id: catalogId,
          authorization_reference: reference.trim(),
          activation_id: activationId.trim(),
          reason: "production_pilot_activation",
          // Echoed from the preflight the operator just read, so the
          // server compares against values a human actually saw. Read
          // from `quote_policies.policies.<name>.value`, the shape the
          // route actually returns and the field the server compares.
          expected_policies: expectedPolicies,
          attestations: ATTESTATIONS.reduce((acc, a) => ({ ...acc, [a.key]: !!attested[a.key] }), {}),
        },
      );
      toast.success("Uber Direct production activated for this tenant.");
      setPreflight(null); setReference(""); setActivationId(""); setAttested({});
      await refreshStatus(company.id);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.code || "Activation refused");
    } finally { setBusy(""); setConfirm(null); }
  };

  const doDisable = async (reason) => {
    setBusy("disable");
    try {
      await api.post("/internal-admin/uber-direct/production-dispatch/disable", {
        company_id: company.id,
        authorization_reference: stopReference.trim(),
        reason,
      });
      toast.success("Production dispatch stopped. Credentials and mode are unchanged.");
      await refreshStatus(company.id);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.code || "Could not stop dispatch");
    } finally { setBusy(""); setConfirm(null); }
  };

  // Server-authoritative provider state, straight off the
  // catalog-selector response. `null` means the server has not answered
  // yet, and is rendered as an em dash rather than "disabled". Reporting
  // unknown as disabled is what previously let the LIVE banner say
  // dispatch was STOPPED on a tenant whose dispatch was running.
  const providerState = selector?.provider || null;
  const providerFlags = selector?.flags || null;
  const effective = selector?.effective || null;
  const stateKnown = !!providerState && !!effective;
  // Strict: only a boolean the server actually sent counts. A missing
  // or malformed field is UNKNOWN, never "disabled"/"STOPPED".
  const dispatchLive = effective && typeof effective.dispatch_live === "boolean"
    ? effective.dispatch_live : null;
  const modeLive = providerState && typeof providerState.mode === "string"
    ? providerState.mode.toLowerCase() === "live" : null;

  const activated = !!status?.activated;
  // Production is judged from the PROVIDER document, not from the
  // staging bookkeeping. Activation releases the staged set's slot so a
  // rotation can be staged; once one is, the status read reports that
  // new set (`activated: false`) while the tenant is still live.
  const productionLive = activated || modeLive === true || dispatchLive === true;
  // The only state that may hide the Emergency Stop: the server has
  // positively answered that this tenant is not in production. Unknown
  // keeps the stop on screen — it is a stop, and cannot make anything live.
  const provenNotProduction = !activated && modeLive === false && dispatchLive === false;
  const dispatchLabel = (on, off) => (dispatchLive === true ? on : dispatchLive === false ? off : "UNKNOWN");
  const staged = !!status?.staged;
  const refsOk = REFERENCE_PATTERN.test(reference.trim());
  const stopRefOk = REFERENCE_PATTERN.test(stopReference.trim());
  const activationIdOk = REFERENCE_PATTERN.test(activationId.trim());
  const allAttested = ATTESTATIONS.every((a) => !!attested[a.key]);

  // Built from the real nested preflight contract. `null` whenever any
  // required policy cannot be authorized, which blocks activation.
  const expectedPolicies = useMemo(
    () => buildExpectedPolicies(preflight?.quote_policies),
    [preflight],
  );
  const blockedPolicies = useMemo(
    () => (preflight ? unauthorizablePolicies(preflight.quote_policies) : []),
    [preflight],
  );

  // The server decides eligibility; this only mirrors it. `overall` is
  // "pass" exactly when no error-severity check failed.
  const preflightPassed = preflight?.overall === "pass";
  const canActivate = !!company && staged && !activated && preflightPassed
    && !!expectedPolicies && blockedPolicies.length === 0
    && !!catalogId && refsOk && activationIdOk && allAttested && !busy;

  const blockers = useMemo(
    () => (preflight?.checks || []).filter(isBlocking),
    [preflight],
  );

  return (
    <InternalAdminLayout>
      <div className="p-6 space-y-6" data-testid="uber-prod-onboarding">
        <header>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Truck className="h-6 w-6" /> Uber Direct Production
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Stage production credentials, run the preflight and activate real Uber Direct
            dispatch for one tenant. Staging and activation are separate actions — staging
            never makes a tenant live.
          </p>
        </header>

        <section className="rounded-lg border p-4">
          <label className="text-sm font-medium" htmlFor="uber-prod-tenant-search">Tenant</label>
          <input
            id="uber-prod-tenant-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name…" data-testid="uber-prod-tenant-search"
            className="mt-1 block w-full max-w-md rounded-md border px-3 py-2 text-sm"
          />
          <ul className="mt-3 max-h-48 divide-y overflow-auto rounded-md border" data-testid="uber-prod-tenant-list">
            {tenants.map((t) => (
              <li key={t.id}>
                <button type="button" onClick={() => selectTenant(t)}
                  data-testid={`uber-prod-tenant-${t.id}`}
                  className={"w-full px-3 py-2 text-left text-sm hover:bg-muted "
                    + (company?.id === t.id ? "bg-muted font-medium" : "")}>
                  {t.name || t.company_name || t.id}
                  <span className="ml-2 font-mono text-xs text-muted-foreground">{t.id}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>

        {company && (
          <>
            <section className="rounded-lg border p-4" data-testid="uber-prod-state">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold">{company.name}</h2>
                <button type="button" onClick={() => refreshStatus(company.id)}
                  className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs"
                  data-testid="uber-prod-refresh">
                  <RefreshCw className="h-3 w-3" /> Refresh
                </button>
              </div>
              <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                <div><dt className="inline text-muted-foreground">Company ID: </dt>
                  <dd className="inline font-mono text-xs" data-testid="uber-prod-company-id">{company.id}</dd></div>
                <div><dt className="inline text-muted-foreground">Uber environment: </dt>
                  <dd className="inline font-medium" data-testid="uber-prod-mode">
                    {providerState ? providerState.mode : "—"}</dd></div>
                <div><dt className="inline text-muted-foreground">Local Delivery: </dt>
                  <dd className="inline" data-testid="uber-prod-local-delivery">
                    {providerFlags
                      ? (providerFlags.local_delivery_enabled ? "enabled" : "disabled")
                      : "—"}</dd></div>
                <div><dt className="inline text-muted-foreground">Uber quotes: </dt>
                  <dd className="inline" data-testid="uber-prod-quotes">
                    {effective
                      ? (effective.quote_path_enabled ? "enabled" : "disabled")
                      : "—"}</dd></div>
                <div><dt className="inline text-muted-foreground">Uber dispatch: </dt>
                  <dd className="inline" data-testid="uber-prod-dispatch">
                    {effective ? dispatchLabel("ENABLED", "disabled") : "—"}</dd></div>
                <div><dt className="inline text-muted-foreground">Production credentials: </dt>
                  <dd className="inline" data-testid="uber-prod-cred-state">
                    {staged || activated ? "Configured" : "Not configured"}</dd></div>
                <div><dt className="inline text-muted-foreground">Staged set status: </dt>
                  <dd className="inline" data-testid="uber-prod-staged-status">
                    {status?.effective_status || "none"}</dd></div>
                <div><dt className="inline text-muted-foreground">Staged by: </dt>
                  <dd className="inline">{status?.credential_set?.created_by || "—"}
                    {status?.credential_set?.created_at ? ` · ${status.credential_set.created_at}` : ""}</dd></div>
              </dl>

              {productionLive ? (
                <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-800"
                   data-testid="uber-prod-live-banner">
                  Environment: {providerState && providerState.mode ? String(providerState.mode).toUpperCase() : "UNKNOWN"}
                  {" · "}Production dispatch: {dispatchLabel("ENABLED", "STOPPED")}
                  {staged && !activated && (
                    <span className="block text-xs font-normal" data-testid="uber-prod-rotation-note">
                      Replacement credentials are staged and NOT yet active; production is running on
                      the current credentials.
                    </span>
                  )}
                </p>
              ) : !stateKnown ? (
                <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900"
                   data-testid="uber-prod-unknown-banner">
                  Production state UNKNOWN — the server's provider state could not be read.
                  Do not assume production is inactive.
                </p>
              ) : staged ? (
                <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"
                   data-testid="uber-prod-staged-banner">
                  Production credentials staged · Production dispatch NOT active
                </p>
              ) : null}
            </section>

            <section className="rounded-lg border p-4">
              <h2 className="flex items-center gap-2 font-semibold">
                <KeyRound className="h-4 w-4" /> Stage production credentials
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                Values are write-only. Nothing stored is ever sent back to this page —
                only whether each field is configured. Staging does not activate production.
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {CREDENTIAL_FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className="text-sm" htmlFor={`uber-prod-${f.key}`}>{f.label}</label>
                    <input
                      id={`uber-prod-${f.key}`} type="password" autoComplete="off"
                      data-testid={`uber-prod-input-${f.key}`}
                      value={secrets[f.key] || ""}
                      onChange={(e) => setSecrets((s) => ({ ...s, [f.key]: e.target.value }))}
                      className="mt-1 block w-full rounded-md border px-3 py-2 text-sm"
                    />
                    <span className="text-xs text-muted-foreground">
                      {status?.credential_set?.fields_present?.[f.key] ? "Configured" : "Not configured"}
                    </span>
                  </div>
                ))}
              </div>
              <button type="button" disabled={!allSecretsPresent || !!busy}
                data-testid="uber-prod-stage-btn"
                onClick={() => setConfirm({ kind: "stage" })}
                className="mt-4 rounded-md bg-stone-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                Stage production credentials
              </button>
            </section>

            <section className="rounded-lg border p-4">
              <h2 className="font-semibold">Preflight</h2>
              <div className="mt-2 flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-sm" htmlFor="uber-prod-catalog">Catalog</label>
                  <select id="uber-prod-catalog" value={catalogId} data-testid="uber-prod-catalog"
                    onChange={(e) => { setCatalogId(e.target.value); setPreflight(null); }}
                    className="mt-1 block rounded-md border px-3 py-2 text-sm">
                    <option value="">Select a catalog…</option>
                    {/* Never hidden — an operator who cannot see the catalog
                        the guard will check cannot diagnose the refusal.
                        Labelled instead, with the reason the server will
                        give. */}
                    {(selector?.catalogs || []).map((c) => {
                      const id = c.id || c.catalog_id;
                      const notes = [];
                      if (c.archived === true) notes.push("archived");
                      if (c.delivery_enabled === false) notes.push("delivery off");
                      return (
                        <option key={id} value={id}>
                          {c.name || id}{notes.length ? ` \u2014 ${notes.join(", ")}` : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <button type="button" onClick={doPreflight} disabled={!catalogId || !!busy}
                  data-testid="uber-prod-preflight-btn"
                  className="rounded-md border px-4 py-2 text-sm font-medium disabled:opacity-50">
                  {busy === "preflight" ? "Running…" : "Run Production Preflight"}
                </button>
                {preflight && (
                  <span data-testid="uber-prod-preflight-overall"
                    className={"rounded px-2 py-1 text-xs font-semibold "
                      + (preflightPassed ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800")}>
                    {preflight.overall === "pass" ? "PASS" : `FAIL · ${preflight.failure_count} blocking`}
                  </span>
                )}
              </div>

              {/* WHICH credentials the server just proved. Not a gate and
                  not a client-side judgement — the server reports it, and
                  an operator must never have to infer whether the run
                  authenticated the staged PRODUCTION credentials or the
                  tenant's existing ones. */}
              {preflight?.evaluation_basis && (
                <p className="mt-2 text-xs text-muted-foreground"
                   data-testid="uber-prod-preflight-basis">
                  {preflight.evaluation_basis.staged_credential_set
                    ? `Evaluated the STAGED production credential set as ${preflight.evaluation_basis.evaluated_mode} `
                      + `— the tenant is still ${preflight.evaluation_basis.provider_mode_now} until activation.`
                    : `Evaluated the tenant's installed credentials as ${preflight.evaluation_basis.evaluated_mode}.`}
                </p>
              )}

              {preflight && (
                <ul className="mt-4 divide-y rounded-md border" data-testid="uber-prod-checks">
                  {(preflight.checks || []).map((c) => (
                    <li key={c.id} className="flex gap-2 px-3 py-2 text-sm" data-testid={`uber-prod-check-${c.id}`}>
                      <Severity check={c} />
                      <div>
                        <p className="font-medium">{c.label}</p>
                        {c.detail ? <p className="text-xs text-muted-foreground">{c.detail}</p> : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="rounded-lg border border-red-200 p-4">
              <h2 className="flex items-center gap-2 font-semibold text-red-800">
                <ShieldAlert className="h-4 w-4" /> Activate production
              </h2>
              {!preflight && (
                <p className="mt-1 text-sm text-muted-foreground" data-testid="uber-prod-activate-hint">
                  Run the preflight first. Activation stays disabled until it passes.
                </p>
              )}
              {preflight && !preflightPassed && (
                <p className="mt-1 text-sm text-red-700" data-testid="uber-prod-activate-blocked">
                  Preflight reported {blockers.length} blocking failure
                  {blockers.length === 1 ? "" : "s"} — resolve
                  {" "}{blockers.map((b) => b.id).join(", ")} before activating.
                </p>
              )}
              {/* A quote policy the server will not accept as authorized is
                  its own blocker, separate from a failing check: the
                  activation comparison refuses a malformed or unenforceable
                  policy, and an unset boolean one. Naming which one
                  and why is the whole point — the credential-safe route
                  redacts field names from its 422, so a refusal that got
                  as far as the server would tell the operator nothing. */}
              {preflight && blockedPolicies.length > 0 && (
                <p className="mt-1 text-sm text-red-700" data-testid="uber-prod-policy-blocked">
                  {blockedPolicies.length} quote{" "}
                  {blockedPolicies.length === 1 ? "policy is" : "policies are"} not
                  authorizable —{" "}
                  {blockedPolicies.map((b) => `${b.label} (${b.status})`).join(", ")}.
                  Correct the tenant's Local Delivery configuration and re-run the preflight.
                  Activation stays disabled until every policy is ready, or
                  (for a numeric limit) intentionally not configured.
                </p>
              )}
              {preflight && expectedPolicies && (
                <dl className="mt-2 grid gap-x-6 text-xs text-muted-foreground sm:grid-cols-2"
                    data-testid="uber-prod-expected-policies">
                  {POLICY_FIELDS.map((f) => (
                    <div key={f.key}>
                      <dt className="inline">{f.label}: </dt>
                      <dd className="inline font-mono" data-testid={`uber-prod-policy-${f.key}`}>
                        {expectedPolicies[f.key] === null
                          ? "not configured — no Rudras-AI limit (null)"
                          : String(expectedPolicies[f.key])}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}

              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-sm" htmlFor="uber-prod-ref">Change-ticket reference</label>
                  <input id="uber-prod-ref" value={reference} data-testid="uber-prod-ref"
                    onChange={(e) => setReference(e.target.value.toUpperCase())}
                    placeholder="UBER-PILOT-20260928-001"
                    className="mt-1 block w-full rounded-md border px-3 py-2 font-mono text-sm" />
                </div>
                <div>
                  <label className="text-sm" htmlFor="uber-prod-activation-id">Activation ID (single use)</label>
                  <input id="uber-prod-activation-id" value={activationId} data-testid="uber-prod-activation-id"
                    onChange={(e) => setActivationId(e.target.value.toUpperCase())}
                    placeholder="UBER-ACT-20260928-001"
                    className="mt-1 block w-full rounded-md border px-3 py-2 font-mono text-sm" />
                </div>
              </div>

              <fieldset className="mt-4">
                <legend className="text-sm font-medium">Operator attestations</legend>
                <p className="text-xs text-muted-foreground">
                  The server cannot establish these. All are required.
                </p>
                <ul className="mt-2 space-y-2">
                  {ATTESTATIONS.map((a) => (
                    <li key={a.key} className="flex gap-2 text-sm">
                      <input type="checkbox" id={`att-${a.key}`} className="mt-1"
                        data-testid={`uber-prod-attest-${a.key}`}
                        checked={!!attested[a.key]}
                        onChange={(e) => setAttested((s) => ({ ...s, [a.key]: e.target.checked }))} />
                      <label htmlFor={`att-${a.key}`}>{a.label}</label>
                    </li>
                  ))}
                </ul>
              </fieldset>

              <button type="button" disabled={!canActivate} data-testid="uber-prod-activate-btn"
                onClick={() => setConfirm({ kind: "activate" })}
                className="mt-4 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                Activate Uber Production
              </button>
            </section>

            {!provenNotProduction && (
              <section className="rounded-lg border p-4" data-testid="uber-prod-stop-section">
                <h2 className="font-semibold">Emergency Disable Production Dispatch</h2>
                {!productionLive && (
                  <p className="mt-1 text-xs font-medium text-amber-800" data-testid="uber-prod-stop-unknown">
                    Production state could not be confirmed, so this control stays available.
                  </p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  Stops NEW production dispatch for this tenant. It does not erase production
                  credentials, does not revert the environment to sandbox, and deliberately keeps
                  production webhook verification working for deliveries already created.
                </p>
                <div className="mt-3 max-w-md">
                  <label className="text-sm" htmlFor="uber-prod-stop-ref">Change-ticket reference</label>
                  <input id="uber-prod-stop-ref" value={stopReference} data-testid="uber-prod-stop-ref"
                    onChange={(e) => setStopReference(e.target.value.toUpperCase())}
                    placeholder="UBER-STOP-20260928-001"
                    className="mt-1 block w-full rounded-md border px-3 py-2 font-mono text-sm" />
                </div>
                <button type="button" disabled={!stopRefOk || busy === "disable"} data-testid="uber-prod-disable-btn"
                  onClick={() => setConfirm({ kind: "disable" })}
                  className="mt-3 rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 disabled:opacity-50">
                  Emergency Disable Production Dispatch
                </button>
              </section>
            )}

            <section className="rounded-lg border p-4">
              <h2 className="font-semibold">Production webhook</h2>
              <p className="mt-1 text-sm">
                Register this route in the merchant's Uber Direct <strong>production</strong> account:
              </p>
              <p className="mt-1 font-mono text-sm" data-testid="uber-prod-webhook-route">
                POST {String(process.env.REACT_APP_BACKEND_URL || "").trim()
                  ? `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/uber-direct`
                  : "<public backend base URL>/api/webhooks/uber-direct"}
              </p>
              {!String(process.env.REACT_APP_BACKEND_URL || "").trim() && (
                <p className="mt-1 text-xs text-amber-800" data-testid="uber-prod-webhook-unknown">
                  This build has no configured backend URL, so the public hostname cannot be shown
                  here — use the public base URL of the backend that serves this tenant.
                </p>
              )}
              <p className="mt-2 text-xs text-muted-foreground">
                Uber generates the signing key when the webhook is created; stage that key above.
                It is never displayed here.
              </p>
            </section>
          </>
        )}

        <ConfirmDialog
          open={confirm?.kind === "stage"} tone="neutral" busy={busy === "stage"}
          title="Stage production credentials"
          confirmLabel="Stage credentials"
          onCancel={() => setConfirm(null)} onConfirm={doStage}
          body={<>
            <p>Tenant: <strong>{company?.name}</strong></p>
            <p>Company ID: <span className="font-mono text-xs">{company?.id}</span></p>
            <p>Environment: <strong>PRODUCTION</strong></p>
            {productionLive ? (
              <p className="font-medium text-red-800" data-testid="uber-prod-stage-live-warning">
                This tenant is LIVE in production. Staging stores REPLACEMENT credentials only;
                production keeps running on the current credentials until a separate activation.
              </p>
            ) : (
              <p className="text-muted-foreground">
                This stores the credentials encrypted and does <strong>not</strong> activate
                production. Uber dispatch stays disabled until you activate it separately.
              </p>
            )}
          </>}
        />

        <ConfirmDialog
          open={confirm?.kind === "activate"} tone="danger" busy={busy === "activate"}
          title="Activate REAL Uber Direct production"
          confirmLabel="Activate production"
          onCancel={() => setConfirm(null)} onConfirm={doActivate}
          body={<>
            <p>Tenant: <strong>{company?.name}</strong></p>
            <p>Company ID: <span className="font-mono text-xs">{company?.id}</span></p>
            <p className="font-medium text-red-700">
              You are about to enable REAL Uber Direct production dispatch for this tenant.
              Real courier requests may be created and real Uber charges may occur after activation.
            </p>
            <p className="text-muted-foreground">Reference {reference} · Activation {activationId}</p>
          </>}
        />

        <ConfirmDialog
          open={confirm?.kind === "disable"} tone="danger" busy={busy === "disable"}
          title="Stop production dispatch"
          confirmLabel="Stop dispatch"
          onCancel={() => setConfirm(null)}
          onConfirm={() => doDisable("emergency_dispatch_disable")}
          body={<>
            <p>Tenant: <strong>{company?.name}</strong></p>
            <p>Company ID: <span className="font-mono text-xs">{company?.id}</span></p>
            <p>This stops NEW production dispatch. Credentials are kept, the environment stays
              LIVE, and deliveries already created keep reporting through the production webhook.</p>
          </>}
        />
      </div>
    </InternalAdminLayout>
  );
}
